//
// # Graphics Entity
//
// Turns a list of graphics elements into a structured combination of sub `Entitys`,
// retaining references to each sub-element.
//

import { graphics, exhaust, randomId } from "GenAlphaModel";
import * as style from "../../style";
import { EntityKind } from "../../entity";
import { type Parent } from "../../tree";
import type { DrawTo } from "../../draw";
import {
  Primitive,
  Rect as RectEntity,
  Path as PathEntity,
  Polygon as PolygonEntity,
  Text as TextEntity,
  Arc as ArcEntity,
  Ellipse as EllipseEntity,
} from "../../primitives";

export type Graphics = Array<graphics.Element>;

// # Graphics Entity
//
// Turns a list of graphics elements into a structured combination of sub `Entitys`,
// retaining references to each sub-element.
//
// Note this is one of the few GUI `Node` types which is not an `Entity`,
// i.e. it is no "updateable" with new data
//
export class GraphicsEntity {
  //
  private constructor(
    readonly parent: Parent, // Parent
    readonly table: style.StyleTable
  ) {}
  get root() {
    return this.parent.root;
  }
  get xform() {
    return this.parent.xform;
  }

  // Type-based child containers
  readonly rects = new Set<RectEntity>();
  readonly paths = new Set<PathEntity>();
  readonly polygons = new Set<PolygonEntity>();
  readonly arcs = new Set<ArcEntity>();
  readonly ellipses = new Set<EllipseEntity>();
  readonly texts = new Set<TextEntity>();

  static new(parent: Parent, table: style.StyleTable) {
    return new GraphicsEntity(parent, table);
  }

  static create(
    data: Graphics,
    parent: Parent,
    table: style.StyleTable
  ): GraphicsEntity {
    const drawer = GraphicsEntity.new(parent, table);
    data.map((elem) => drawer.add(elem));
    return drawer;
  }

  // FIXME: maybe theres some type-system-fu to make this more general? But just doing it manually for now.
  addRect = (data: graphics.Rectangle): RectEntity => {
    const drawer = new RectEntity({
      data,
      parent: this.parent,
      table: this.table.shape,
    });
    return this.addRectEntity(drawer);
  };
  private addRectEntity = (drawer: RectEntity): RectEntity => {
    this.rects.add(drawer);
    return drawer;
  };
  removeRect = (rect: RectEntity): boolean => this.rects.delete(rect);
  addPolygon = (polygon: graphics.Polygon): PolygonEntity => {
    const drawer = new PolygonEntity({
      data: polygon,
      parent: this.parent,
      table: this.table.shape,
    });
    return this.addPolygonEntity(drawer);
  };
  private addPolygonEntity = (drawer: PolygonEntity): PolygonEntity => {
    this.polygons.add(drawer);
    return drawer;
  };
  removePolygon = (polygon: PolygonEntity): boolean =>
    this.polygons.delete(polygon);
  addPath = (path: graphics.Path): PathEntity => {
    const drawer = new PathEntity({
      data: path,
      parent: this.parent,
      table: this.table.shape,
    });
    return this.addPathEntity(drawer);
  };
  private addPathEntity = (drawer: PathEntity): PathEntity => {
    this.paths.add(drawer);
    return drawer;
  };
  removePath = (path: PathEntity): boolean => this.paths.delete(path);
  addEllipse = (ellipse: graphics.Ellipse): EllipseEntity => {
    const drawer = new EllipseEntity({
      data: ellipse,
      parent: this.parent,
      table: this.table.shape,
    });
    return this.addEllipseEntity(drawer);
  };
  private addEllipseEntity = (drawer: EllipseEntity): EllipseEntity => {
    this.ellipses.add(drawer);
    return drawer;
  };
  removeEllipse = (ellipse: EllipseEntity): boolean =>
    this.ellipses.delete(ellipse);

  addArc = (arc: graphics.Arc): ArcEntity => {
    const arcEntity = new ArcEntity({
      data: arc,
      parent: this.parent,
      table: this.table.shape,
    });
    return this.addArcEntity(arcEntity);
  };
  addArcEntity = (drawer: ArcEntity): ArcEntity => {
    this.arcs.add(drawer);
    return drawer;
  };
  removeArcEntity = (drawer: ArcEntity): boolean => {
    return this.arcs.delete(drawer);
  };

  addText = (text: graphics.Text): TextEntity => {
    const textEntity = new TextEntity({
      data: text,
      parent: this.parent,
      table: this.table.text,
    });
    return this.addTextEntity(textEntity);
  };
  addTextEntity = (drawer: TextEntity): TextEntity => {
    this.texts.add(drawer);
    return drawer;
  };
  removeTextEntity = (drawer: TextEntity): boolean => {
    return this.texts.delete(drawer);
  };

  addEntity = (child: Primitive): boolean => {
    if (child.parent !== this.parent) return false; // Not adding stuff from other parents
    this.addEntity_(child); // Do the inner implementation
    return true; // And indicate that we succeeded
  };
  private addEntity_ = (child: Primitive): Primitive => {
    const { entityKind } = child;
    switch (entityKind) {
      case EntityKind.Rect:
        return this.addRectEntity(child);
      case EntityKind.Polygon:
        return this.addPolygonEntity(child);
      case EntityKind.Path:
        return this.addPathEntity(child);
      case EntityKind.Ellipse:
        return this.addEllipseEntity(child);
      case EntityKind.Arc:
        return this.addArcEntity(child);
      case EntityKind.Text:
        return this.addTextEntity(child);
      default:
        exhaust(entityKind);
    }
  };

  remove = (child: Primitive): boolean => {
    const { entityKind } = child;
    switch (entityKind) {
      case EntityKind.Rect:
        return this.removeRect(child);
      case EntityKind.Polygon:
        return this.removePolygon(child);
      case EntityKind.Path:
        return this.removePath(child);
      case EntityKind.Ellipse:
        return this.removeEllipse(child);
      case EntityKind.Arc:
        return this.removeArcEntity(child);
      case EntityKind.Text:
        return this.removeTextEntity(child);
      default:
        return false;
    }
  };

  add = (element: graphics.Element): unknown => {
    // Ensure every element has an ID before it enters the change pipeline.
    // IDs persist in element.data and are used for per-element change tracking.
    if (element.id === undefined) {
      element.id = randomId();
    }
    const { Kind } = graphics.Element;
    switch (element.kind) {
      case Kind.Rectangle:
        return this.addRect(element);
      case Kind.Polygon:
        return this.addPolygon(element);
      case Kind.Path:
        return this.addPath(element);
      case Kind.Ellipse:
        return this.addEllipse(element);
      case Kind.Arc:
        return this.addArc(element);
      case Kind.Text:
        return this.addText(element);
      default:
        exhaust(element);
    }
  };

  redraw = (to: DrawTo): unknown => {
    // Re-draw all our structured elements
    this.rects.forEach((r) => r.redraw(to));
    this.paths.forEach((r) => r.redraw(to));
    this.polygons.forEach((r) => r.redraw(to));
    this.arcs.forEach((r) => r.redraw(to));
    this.ellipses.forEach((r) => r.redraw(to));
    this.texts.forEach((r) => r.redraw(to));

    return null;
  };

  // Spit out a list of our data elements
  toData = (): Graphics => {
    const data: Graphics = [];
    this.rects.forEach((r) => data.push(r.data));
    this.paths.forEach((r) => data.push(r.data));
    this.polygons.forEach((r) => data.push(r.data));
    this.arcs.forEach((r) => data.push(r.data));
    this.ellipses.forEach((r) => data.push(r.data));
    this.texts.forEach((r) => data.push(r.data));
    return data;
  };
}
